'use client'

import { useState } from 'react'
import NewsletterModal, { type NewsletterContent } from '@/components/NewsletterModal'
import { ou } from '@/lib/contenuEditable'

/**
 * CTA finale fermant la home — pattern double action (Devenir membre +
 * Rester informé). Le texte s'adapte selon `mode` :
 *   - upcoming (défaut) : invite à entrer dans le Club pour les
 *     prochains événements
 *   - past : remerciement après une soirée tenue + invitation à
 *     l'inscription newsletter pour ne pas manquer le prochain événement
 *
 * Les deux boutons restent identiques dans les deux modes ; ils mènent
 * tous les deux à un objectif "next event" → la promesse business est
 * la même, seul le ton change. */

/**
 * Contenu éditable depuis le CRM — section `cta-cercle` de la home.
 *
 * Les textes existent en DEUX versions parce que le bloc en change selon le
 * statut de l'événement. Les éditer séparément évite d'avoir à repasser dans
 * l'éditeur le lendemain de chaque soirée.
 */
export interface CercleCtaContent {
  eyebrow?: string
  title?: string
  subtitle?: string
  titlePast?: string
  subtitlePast?: string
  primaryButton?: { label?: string; href?: string }
  secondaryButton?: { label?: string }
}

interface CercleCtaSectionProps {
  mode?: 'upcoming' | 'past'
  content?: CercleCtaContent
  /* Textes de la fenêtre d'inscription, qui n'a pas de section à elle : elle
     est ouverte depuis ici et depuis le menu. */
  newsletter?: NewsletterContent
}

export default function CercleCtaSection({ mode = 'upcoming', content, newsletter }: CercleCtaSectionProps) {
  const [modalOpen, setModalOpen] = useState(false)

  const isPast = mode === 'past'
  const eyebrow = ou(content?.eyebrow, "Club Démo")
  const title = isPast
    ? ou(content?.titlePast, "Merci d'avoir fait de cette soirée un moment d'exception")
    : ou(content?.title, 'Entrez dans le Club')
  const paragraph = isPast
    ? ou(content?.subtitlePast, 'Inscrivez-vous pour ne manquer aucun de nos prochains événements')
    : ou(content?.subtitle, 'Rejoignez les membres ou suivez nos prochaines rencontres')
  const ctaLabel = ou(content?.primaryButton?.label, 'Devenir membre')
  const ctaHref = ou(content?.primaryButton?.href, '/devenir-membre')
  /* Ce bouton ouvre la fenêtre d'inscription : son libellé est éditable,
     mais pas sa destination — il n'a pas d'URL. */
  const ctaSecondaireLabel = ou(content?.secondaryButton?.label, 'Rester informé')

  // Option image de fond : le Club devient un bloc immersif (photo + voile marine),
  // texte en blanc. Sans image → reste sur crème + encre marine.
  const bgImage = (content as { backgroundImage?: string })?.backgroundImage
  const onImage = typeof bgImage === 'string' && bgImage.trim() !== ''

  return (
    <>
      {/* Club immersif : texte sur l'image, section haute (plein impact). */}
      <section className="relative overflow-hidden flex items-center" style={{ backgroundColor: '#F7EFE1', minHeight: '82vh' }}>
        {onImage && (
          <div aria-hidden className="absolute inset-0" style={{ zIndex: 0 }}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={bgImage} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(18, 20, 30, 0.5)' }} />
          </div>
        )}
        <div className="relative max-w-3xl mx-auto px-6 md:px-8 text-center py-24 md:py-28" style={{ zIndex: 1 }}>
          <div className="mb-6 md:mb-8">
            <span
              className="inline-block text-[11px] font-medium uppercase"
              style={{
                color: onImage ? '#e8dcc2' : 'var(--color-brand-ink)',
                letterSpacing: '0.32em',
                borderBottom: `1px solid ${onImage ? 'rgba(232,220,194,0.5)' : 'rgba(22, 36, 58, 0.22)'}`,
                padding: '0 14px 6px',
              }}
            >
              {eyebrow}
            </span>
          </div>

          <h2
            className="font-normal leading-tight mb-5 uppercase"
            style={{
              fontFamily: 'var(--font-heading, inherit)',
              fontWeight: onImage ? 600 : 500,
              fontSize: 'clamp(2.2rem, 1.4rem + 3vw, 3.8rem)',
              letterSpacing: '0.04em',
              color: onImage ? '#ffffff' : 'rgba(22, 36, 58, 0.96)',
              textShadow: onImage ? '0 2px 24px rgba(0,0,0,0.4)' : undefined,
            }}
          >
            {title}
          </h2>

          <p
            className="font-normal max-w-xl mx-auto mb-10 md:mb-12"
            style={{
              fontSize: 'clamp(0.92rem, 0.85rem + 0.15vw, 1rem)',
              lineHeight: 1.7,
              color: onImage ? 'rgba(255,255,255,0.9)' : 'rgba(22, 36, 58, 0.85)',
            }}
          >
            {paragraph}
          </p>

          <div className="flex flex-row gap-3 sm:gap-8 justify-center items-center">
            <a href={ctaHref} className={onImage ? 'gde-btn gde-btn--on-dark' : 'gde-btn'}>
              {ctaLabel}
            </a>
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className={onImage ? 'gde-btn gde-btn--on-dark' : 'gde-btn'}
            >
              {ctaSecondaireLabel}
            </button>
          </div>
        </div>
      </section>

      <NewsletterModal open={modalOpen} onClose={() => setModalOpen(false)} content={newsletter} />
    </>
  )
}
